/**
 * ClaimDrawer — bottom sheet listing claimable milestone rewards.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trophy, Gift, Heart, Users, Award, Flame, Sparkles, Plus, NotebookPen } from "lucide-react";
import { usePendingRewards, type PendingReward } from "@/hooks/usePendingRewards";

const ICON_MAP: Record<string, React.ReactNode> = {
  create_first_goal:       <Trophy className="h-6 w-6" />,
  first_wishlist_item:     <Heart className="h-6 w-6" />,
  first_wishlist_received: <Gift className="h-6 w-6" />,
  first_gift_sent:         <Gift className="h-6 w-6" />,
  first_connection:        <Users className="h-6 w-6" />,
  generate_certificate:    <Award className="h-6 w-6" />,
  daily_streak_3:          <Flame className="h-6 w-6" />,
  daily_streak_7:          <Flame className="h-6 w-6" />,
  daily_streak_30:         <Flame className="h-6 w-6" />,
};

const TITLE_MAP: Record<string, string> = {
  create_first_goal: "First Goal Created",
  first_wishlist_item: "First Gift List Item",
  first_wishlist_received: "First Gift Reserved",
  first_gift_sent: "First Reward Pledged",
  first_connection: "First Connection Made",
  generate_certificate: "First Certificate",
  daily_streak_3: "3-Day Streak",
  daily_streak_7: "7-Day Streak",
  daily_streak_30: "30-Day Streak",
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function ClaimDrawer({ open, onOpenChange }: Props) {
  const { rewards, claim, isClaiming } = usePendingRewards();
  const navigate = useNavigate();

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const sorted = useMemo(
    () => [...rewards].sort((a, b) => b.points - a.points),
    [rewards]
  );

  const handleClaim = async (r: PendingReward) => {
    await claim(r.id);
  };

  const handleClaimAll = async () => {
    for (const r of sorted) {
      // sequential to keep level-up animation consistent
      // eslint-disable-next-line no-await-in-loop
      await claim(r.id);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl max-h-[85vh] flex flex-col"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))", touchAction: "manipulation" }}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Rewards to claim
          </SheetTitle>
          <SheetDescription>
            Tap CLAIM to add the points to your total.
          </SheetDescription>
        </SheetHeader>

        {sorted.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-8 px-2 text-center">
            <div className="h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center mb-3">
              <Trophy className="h-8 w-8 text-amber-500" />
            </div>
            <p className="font-semibold mb-1">No rewards waiting</p>
            <p className="text-sm text-muted-foreground mb-5 max-w-xs">
              Complete a goal, generate a certificate, or reflect on your day to unlock points you can claim here.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full max-w-sm">
              <Button
                variant="outline"
                size="sm"
                onClick={() => go('/')}
                className="justify-start gap-2"
              >
                <Plus className="h-4 w-4 text-primary" />
                Create goal
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => go('/certificate/new')}
                className="justify-start gap-2"
              >
                <Award className="h-4 w-4 text-amber-500" />
                New certificate
              </Button>
            </div>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6 mt-2">
              <ul className="space-y-2">
                {sorted.map((r) => (
                  <li
                    key={r.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border bg-card"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        {ICON_MAP[r.action_type] ?? <Trophy className="h-6 w-6" />}
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="font-semibold text-sm truncate">
                          {TITLE_MAP[r.action_type] ?? r.description ?? "Milestone"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          +{r.points} pts
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      disabled={isClaiming}
                      onClick={() => handleClaim(r)}
                      className="font-bold tracking-wide w-full sm:w-auto sm:ml-auto sm:flex-shrink-0"
                    >
                      CLAIM
                    </Button>
                  </li>
                ))}
              </ul>
            </ScrollArea>

            {sorted.length > 1 && (
              <div className="pt-3 border-t mt-3">
                <Button
                  className="w-full"
                  variant="secondary"
                  disabled={isClaiming}
                  onClick={handleClaimAll}
                >
                  Claim all ({sorted.reduce((s, r) => s + r.points, 0)} pts)
                </Button>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
